<template>
    <page-controls>
        <template v-slot:left>
            <div class="grow @3xl/controls:max-w-md">
                <vx-filterable :active="active" @toggle="toggleFilterable" data-test="contact-filters">
                    <template v-slot:placeholder>
                        <span class="mr-2">Find Contacts</span>
                        <vx-badge v-if="filtersCount > 0" :borders="false" size="xs" @click.stop="reset">
                            {{ pluralized('filter', filtersCount, true) }}
                            <font-awesome-icon widthAuto :icon="faCircleXmark" />
                        </vx-badge>
                    </template>
                    <template v-slot:form>
                        <form @submit.prevent="apply" class="flex flex-col gap-4">
                            <div>
                                <filter-label
                                    :canReset="!!filters['filter[search]']"
                                    @reset="filters['filter[search]'] = undefined"
                                >
                                    {{
                                        isLargeTeamSearchRestricted(teamId, props.groupId)
                                            ? 'Phone or Email'
                                            : 'Keyword Search'
                                    }}
                                </filter-label>
                                <vx-input
                                    type="text"
                                    data-test="filters-query"
                                    placeholder="Search"
                                    v-model="filters['filter[search]']"
                                    maxlength="191"
                                    size="lg"
                                >
                                    <template v-slot:right>
                                        <font-awesome-icon widthAuto class="text-xs" :icon="faMagnifyingGlass" />
                                    </template>
                                </vx-input>
                            </div>

                            <div>
                                <filter-label
                                    :canReset="filters['filter[marketing_subscription_status]'] !== undefined"
                                    @reset="filters['filter[marketing_subscription_status]'] = undefined"
                                >
                                    Marketing Subscription
                                </filter-label>
                                <vx-selectable
                                    placeholder="Status"
                                    :clearable="false"
                                    :options="[
                                        { label: 'Opt-in', value: 'opt_in' },
                                        { label: 'Opt-out', value: 'opt_out' },
                                        { label: 'No preference', value: 'unknown' },
                                    ]"
                                    v-model="filters['filter[marketing_subscription_status]']"
                                    :reduce="(option) => option.value"
                                    size="lg"
                                    excludeSelected
                                >
                                </vx-selectable>
                            </div>

                            <div>
                                <filter-label
                                    :canReset="filters['filter[tag_name]'] !== undefined"
                                    @reset="filters['filter[tag_name]'] = undefined"
                                >
                                    Tag
                                </filter-label>
                                <vx-selectable
                                    placeholder="Name"
                                    :clearable="false"
                                    :options="tags"
                                    :loading="searchingTags"
                                    :taggable="true"
                                    @search="searchTags"
                                    v-model="filters['filter[tag_name]']"
                                    size="lg"
                                    excludeSelected
                                >
                                </vx-selectable>
                            </div>

                            <div v-if="FEATURES.contact_groups && props.groupId">
                                <filter-label
                                    :canReset="filters['filter[is_preferred]'] !== undefined"
                                    @reset="
                                        () => {
                                            isPreferred = false;
                                            notPreferred = false;
                                            filters['filter[is_preferred]'] = undefined;
                                        }
                                    "
                                >
                                    Preferred
                                </filter-label>

                                <div class="w-full flex items-center justify-between gap-1">
                                    <vx-checkbox-selection-item
                                        :active="isPreferred"
                                        @selected="isPreferred = !isPreferred"
                                        class="grow"
                                    >
                                        Is Preferred
                                    </vx-checkbox-selection-item>
                                    <vx-checkbox-selection-item
                                        :active="notPreferred"
                                        @selected="notPreferred = !notPreferred"
                                        class="grow"
                                    >
                                        Not Preferred
                                    </vx-checkbox-selection-item>
                                </div>
                            </div>

                            <div class="flex gap-2 pt-4">
                                <vx-button type="button" color="muted-light" size="lg" @click="cancel">
                                    Cancel
                                </vx-button>
                                <vx-button data-test="filters-apply" size="lg" type="submit" block>
                                    Apply
                                    <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                                </vx-button>
                            </div>
                        </form>
                    </template>
                </vx-filterable>
            </div>
            <div class="flex w-full gap-2 @xl/controls:w-auto">
                <vx-button-group size="lg" class="w-full">
                    <vx-select
                        v-model="filters.sort_field"
                        @update:modelValue="apply"
                        :options="[
                            {
                                value: 'id',
                                label: 'Sort by: Created',
                            },
                            {
                                value: 'phone',
                                label: 'Sort by: Phone',
                            },
                        ]"
                    ></vx-select>
                    <vx-button
                        @click.prevent="
                            filters.sort_dir = filters.sort_dir === 'asc' ? 'desc' : 'asc';
                            apply();
                        "
                        type="button"
                        color="white"
                        shadow
                    >
                        <font-awesome-icon
                            widthAuto
                            :icon="filters.sort_dir === 'asc' ? faArrowUpWideShort : faArrowDownWideShort"
                        ></font-awesome-icon>
                    </vx-button>
                </vx-button-group>
            </div>
        </template>
        <template v-slot:right>
            <router-button
                block
                :to="
                    props.groupId && onCorporate()
                        ? { name: 'contacts.new', query: { group_id: props.groupId } }
                        : { name: 'contacts.new' }
                "
                size="lg"
                color="primary"
            >
                {{ props.groupId ? 'Add' : 'New' }} Contact
                <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
            </router-button>
        </template>
    </page-controls>
</template>

<script setup>
import { computed, ref, watch, nextTick, inject } from 'vue';
import {
    VxBadge,
    VxButton,
    VxButtonGroup,
    VxFilterable,
    VxInput,
    VxSelectable,
    VxSelect,
    VxCheckboxSelectionItem,
} from '@voxie/frontend-components';
import {
    faCircleCheck,
    faMagnifyingGlass,
    faCircleXmark,
    faArrowDownWideShort,
    faArrowUpWideShort,
    faCirclePlus,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import PageControls from '~/components/general/PageControls.vue';
import RouterButton from '~/components/general/RouterButton.vue';
import { tagsClient } from '~/services';
import debounce from '~/utils/debounce';
import { isLargeTeamSearchRestricted } from '~/utils/team';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import FilterLabel from '~/components/general/FilterLabel.vue';
import { pluralized } from '~/components/filters';

const FEATURES = inject('FEATURES');

const teamId = Spark.state.currentTeam.id;
const { onCorporate } = useCurrentGroup();

const filters = ref({});
const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
    groupId: {
        type: String,
        required: false,
    },
});

const isPreferred = ref(false);
const notPreferred = ref(false);

const setIsPreferred = () => {
    if (!FEATURES.contact_groups || !props.groupId) {
        return;
    }

    if (props.modelValue['filter[is_preferred]'] === 0) {
        isPreferred.value = false;
        notPreferred.value = true;
    } else if (props.modelValue['filter[is_preferred]'] === 1) {
        isPreferred.value = true;
        notPreferred.value = false;
    } else if (props.modelValue['filter[is_preferred]'] === null) {
        isPreferred.value = true;
        notPreferred.value = true;
    } else {
        isPreferred.value = false;
        notPreferred.value = false;
    }
};

watch(
    () => props.modelValue,
    (modelValue) => {
        filters.value = { ...modelValue };
        setIsPreferred();
    },
    {
        deep: true,
        immediate: true,
    }
);

watch(
    [isPreferred, notPreferred],
    () => {
        if (isPreferred.value && !notPreferred.value) {
            filters.value['filter[is_preferred]'] = 1;
        } else if (!isPreferred.value && notPreferred.value) {
            filters.value['filter[is_preferred]'] = 0;
        } else if (isPreferred.value && notPreferred.value) {
            filters.value['filter[is_preferred]'] = null;
        } else {
            filters.value['filter[is_preferred]'] = undefined;
        }
    },
    {
        immediate: true,
    }
);

const emit = defineEmits(['update:modelValue', 'apply', 'reset']);

const active = ref(false);

const toggleFilterable = (event) => {
    if (!tags.value.length && event) {
        searchTags();
    }
    active.value = event;
};

const filtersCount = computed(
    () =>
        (props.modelValue['filter[marketing_subscription_status]'] ? 1 : 0) +
        (props.modelValue['filter[tag_name]'] ? 1 : 0) +
        (props.modelValue['filter[search]'] ? 1 : 0) +
        ([null, 1, 0].includes(props.modelValue['filter[is_preferred]']) ? 1 : 0)
);

const reset = () => {
    active.value = false;
    emit('reset');
};

const cancel = async () => {
    filters.value = { ...props.modelValue };
    setIsPreferred();
    active.value = false;
};

const apply = async () => {
    active.value = false;
    emit('update:modelValue', { ...filters.value });
    await nextTick();
    emit('apply');
};

const tags = ref([]);
const searchingTags = ref(false);
const searchTags = debounce((search) => {
    searchingTags.value = true;
    tagsClient
        .tagsAutocomplete(teamId, {
            query: search,
        })
        .then((response) => {
            tags.value = response.data.data.map((tag) => tag.value);
            searchingTags.value = false;
        });
}, 200);
</script>
